import { LinkedProjectConfig, ReportMetadata, ServiceItem, ServiceTransaction, UserProfile } from '../types';
import { supabase } from './supabaseClient';
import { loadProjectById, saveProjectToCloud } from './cloudService';
import {
    ersetzeJournalzeilen,
    getServiceLog,
    ladeLebendeJournalzeilenZu,
    meldeSpiegelFehler,
    mirrorEntriesToJournal,
    mirrorEntriesToJournalOhneKorb,
    toJournalRows,
    voidJournalEntries,
} from './serviceJournal';
import { autoArchiveVerdict } from './projectAutoArchive';
import { istUebungsprojekt } from './uebung/uebungService';
import { pruefeArchivierbarkeitFuerStand } from './archiveGuardService';
import { isProjectSaveConflict } from './projectVersionAnchor';
import { REVERSAL_REFERENCE_PREFIX, ReversalTarget, planReversal } from './serviceReversalLogic';
import { ServiceLogRemovalOutcome, removeServiceLogEntries } from './serviceLogRemoval';
import { kennungFuerVergleich } from './serviceLogUtils';

/**
 * Belege, deren Zuordnungen NICHT zaehlen: sie sind noch nicht ausgestellt und
 * koennen jederzeit verworfen oder neu aufgebaut werden. Dieselbe Menge wie
 * in der Abweichungssicht (`zuordnungen` in service_journal_write.sql), damit
 * der Client nicht strenger oder lockerer ist als die Kontrolle.
 */
const NICHT_AUSGESTELLT: ReadonlySet<string> = new Set(['DRAFT', 'READY_FOR_REVIEW', 'APPROVED', 'ISSUING']);

type BelegZeile = { projectId?: string | null; allocations?: Array<{ entryId?: string | null; quantity?: number | string | null }> | null };
type BelegRoh = { id: string; status: string | null; primary_project_id: string | null; lines: unknown };

/**
 * Was ausgestellte Rechnungen EINEM Protokolleintrag zugeordnet haben — ueber
 * alle Belege des Projekts, Storno und Gutschrift mit negativem Vorzeichen
 * verrechnet (`allocationLogic.allocatedByEntry` rechnet genauso).
 *
 * Zwei Abfragen statt einer: Sammelrechnungen tragen das Projekt nur in den
 * Zeilen (`lines[].projectId`), nicht in `primary_project_id`; jsonb-
 * Containment findet sie. Eine Zeile ohne `projectId` gehoert zum
 * Primaerprojekt des Belegs — die Regel der Abweichungssicht.
 *
 * WIRFT bei Lesefehlern. Der Aufrufer bricht dann ab: „unbekannt" als 0 zu
 * lesen hiesse, eine abgerechnete Menge freizugeben.
 */
const zugeordneteMengeDesEintrags = async (projectId: string, entryId: string): Promise<number> => {
    const spalten = 'id, status, primary_project_id, lines';
    // Als JSON-STRING, nicht als Array: postgrest-js baut aus einem Array
    // `cs.{a,b}` (Postgres-Array-Syntax) und aus Objekten darin
    // `cs.{[object Object]}` — die Abfrage schlaegt dann fehl. Ein String geht
    // unveraendert als `cs.[{"projectId":"…"}]` durch, und PostgREST wertet
    // ihn fuer eine jsonb-Spalte als Containment (Codex-Befund 23.09.2026).
    const zeilenMitProjekt = JSON.stringify([{ projectId }]);
    const [primaer, inZeilen] = await Promise.all([
        supabase.from('invoices').select(spalten).eq('primary_project_id', projectId),
        supabase.from('invoices').select(spalten).contains('lines', zeilenMitProjekt),
    ]);
    if (primaer.error) throw primaer.error;
    if (inZeilen.error) throw inZeilen.error;

    const belege = new Map<string, BelegRoh>();
    for (const row of [...(primaer.data ?? []), ...(inZeilen.data ?? [])] as BelegRoh[]) {
        if (row?.id) belege.set(row.id, row);
    }

    // Verglichen ueber `kennungFuerVergleich` — dieselbe Regel wie
    // allocatedByEntry: eine uuid, die der Beleg gross nennt und das Protokoll
    // (Journal-Weg) klein, ist derselbe Eintrag. Exakt verglichen ergaebe sie 0,
    // und die Ruecknahme gaebe eine abgerechnete Menge frei.
    const gesucht = kennungFuerVergleich(entryId);
    let summe = 0;
    for (const beleg of belege.values()) {
        if (NICHT_AUSGESTELLT.has(String(beleg.status ?? ''))) continue;
        const zeilen = Array.isArray(beleg.lines) ? (beleg.lines as BelegZeile[]) : [];
        for (const zeile of zeilen) {
            const zeilenProjekt = String(zeile?.projectId ?? '').trim() || String(beleg.primary_project_id ?? '');
            if (zeilenProjekt !== projectId) continue;
            for (const zuordnung of zeile?.allocations ?? []) {
                if (kennungFuerVergleich(zuordnung?.entryId) !== gesucht) continue;
                const menge = Number(zuordnung?.quantity);
                if (Number.isFinite(menge)) summe += menge;
            }
        }
    }
    return Math.round(summe * 1000) / 1000;
};

/**
 * Schreibpfad der RUECKNAHME — die einzige Stelle, die sie persistiert.
 *
 * Dieselbe Aufteilung wie beim Verzicht: die Regeln liegen in
 * `serviceReversalLogic.ts` und sind ohne Supabase pruefbar, hier steht nur,
 * was ohne Datenbank nicht zu haben ist. Geschrieben wird ueber
 * `saveProjectToCloud`, damit BEIDE Quellen der Position (relational
 * `project_services` und die JSON-Spiegelung) denselben Stand tragen.
 *
 * Anders als beim Verzicht wird hier auch das PROTOKOLL veraendert: der
 * falsche Eintrag verschwindet. Das ist der Grund, warum der Plan das
 * vollstaendige neue Protokoll mitbringt und nicht nur einen Zusatz.
 */

const actorNameOf = (user: UserProfile | null | undefined): string =>
    user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Buchhaltung' : 'Buchhaltung';

/**
 * Nach dem Schliessen der letzten offenen Menge das Projekt selbst ins Archiv
 * setzen — die Regeln dafuer stehen in projectAutoArchive und sind streng
 * (siehe dort: lieber gar nicht als zu frueh).
 */
const withAutoArchive = async (
    projectId: string,
    globalData: any,
    services: ServiceItem[],
    serviceLog: ServiceTransaction[],
    now: string,
    actor: string,
): Promise<{ globalData: any; notes: any[]; hinweis: string }> => {
    // Uebungsprojekt (Lernzentrum 8.2): kein Auto-Archiv. Das Kennzeichen steht
    // im Blob, weil dieser Pfad die Projektzeile nicht liest.
    const verdict = autoArchiveVerdict(services, globalData?.status, istUebungsprojekt({ istUebung: globalData?.istUebung }));
    if (!verdict.archive) {
        return { globalData, notes: [], hinweis: verdict.reason ? ` Nicht archiviert: ${verdict.reason}` : '' };
    }
    // ARCHIV-RIEGEL: der dritte Weg ins Archiv — und der einzige ohne
    // Anwenderklick. Ein Projekt mit eigenem offenem Posten bleibt auch hier
    // offen, und eine Mutter mit offenen Toechtern ebenfalls.
    // Gerade hier ist der Riegel noetig: "am LV der Mutter ist nichts mehr
    // offen" ist bei einer laufenden Tochter der NORMALZUSTAND zwischen zwei
    // Rechnungen. Ohne ihn setzte sich die Mutter genau in dem Moment selbst
    // ins Archiv, in dem die Buchhaltung die letzte offene Menge schliesst.
    //
    // Geprueft wird der STAND, DER GLEICH GESPEICHERT WIRD, nicht die
    // Datenbank: die traegt hier noch den Zustand von VOR dem Vorgang, und der
    // Riegel hielte dem Anwender die soeben geschlossene Menge als offen vor —
    // das Auto-Archiv griffe nie (Begruendung an
    // pruefeArchivierbarkeitFuerStand).
    //
    // Der Grund wird durchgereicht statt verschwiegen: "nichts passiert" ist
    // bei einem Automatismus die schlechteste Auskunft (siehe
    // AutoArchiveVerdict.reason). Die Regel steht in archiveGuardLogic.ts.
    const urteil = await pruefeArchivierbarkeitFuerStand(projectId, services, serviceLog);
    if (!urteil.erlaubt) {
        return { globalData, notes: [], hinweis: `\n\n${urteil.meldung}` };
    }
    return {
        globalData: { ...globalData, status: 'completed' },
        notes: [
            {
                id: crypto.randomUUID(),
                text: 'Projekt automatisch auf „Beendet" gesetzt: es ist nichts mehr abrechenbar und keine beauftragte Leistung mehr offen. Ins Archiv geht es, sobald die Schlussrechnung gestellt und bezahlt ist.',
                date: now,
                author: actor,
            },
        ],
        hinweis: ' Das Projekt wurde automatisch auf „Beendet" gesetzt.',
    };
};

/**
 * Kennung einer Protokollzeile fuer den Vorher/Nachher-Vergleich: die id, und
 * wo sie fehlt der Inhalt. Nur so laesst sich aus dem VOLLSTAENDIG neu
 * gebauten `plan.log` herausloesen, welche Zeile wirklich neu ist — an die
 * Arrayposition darf sich hier nichts haengen (siehe `deterministicEntryId`).
 */
const kennung = (e: ServiceTransaction | null | undefined): string =>
    String((e as any)?.id ?? '').trim()
    || [e?.serviceId, e?.date, e?.quantity, e?.description, (e as any)?.reportRef]
        .map((v) => String(v ?? ''))
        .join('|');

const newReference = (): string =>
    `${REVERSAL_REFERENCE_PREFIX}${new Date().getFullYear()}-${Math.floor(Math.random() * 100000)
        .toString()
        .padStart(5, '0')}`;

export interface ReversalCommand {
    projectId: string;
    serviceId: string;
    target: ReversalTarget;
    reason: string;
    user: UserProfile | null;
}

export interface ReversalOutcome {
    ok: boolean;
    message: string;
}

/**
 * Eine Leistungsbuchung zuruecknehmen.
 *
 * Frisch geladen wird IMMER: die Ansicht arbeitet auf einem Abzug vom letzten
 * Refresh, und die Schranken rechnen gegen `quantityBilled`. Auf einem
 * veralteten Stand koennte die Ruecknahme unter eine Menge fallen, die
 * inzwischen abgerechnet wurde.
 */
export const reverseServiceBooking = async (
    command: ReversalCommand,
): Promise<ReversalOutcome> => {
    let fullProject: any;
    try {
        fullProject = await loadProjectById(command.projectId);
    } catch (e: any) {
        return { ok: false, message: `Projekt konnte nicht geladen werden: ${e?.message || e}` };
    }
    if (!fullProject) return { ok: false, message: 'Projekt nicht gefunden.' };

    const meta = fullProject.data?.metadata as ReportMetadata | undefined;
    const services: ServiceItem[] = meta?.globalData?.services || [];
    const target = services.find((s) => s.id === command.serviceId);
    if (!meta || !target) {
        return { ok: false, message: 'Die Position wurde im Projekt nicht gefunden.' };
    }

    // Was ausgestellte Rechnungen dem Eintrag zugeordnet haben — die Schranke
    // je Eintrag (Begruendung an `ReversalInput.allocatedQuantity`). Nicht
    // lesbar heisst nicht 0: dann wird abgebrochen.
    let allocatedQuantity = 0;
    if (command.target.kind === 'entry') {
        try {
            allocatedQuantity = await zugeordneteMengeDesEintrags(fullProject.id, command.target.entryId);
        } catch (e) {
            const text = e instanceof Error ? e.message : String((e as { message?: unknown } | null)?.message ?? e);
            return { ok: false, message: `Die Rechnungszuordnungen des Eintrags konnten nicht geladen werden: ${text}` };
        }
    }

    const now = new Date().toISOString();
    const reference = newReference();
    const result = planReversal(target, getServiceLog({ metadata: meta }), {
        target: command.target,
        reason: command.reason,
        actorName: actorNameOf(command.user),
        actorId: command.user?.id,
        now,
        traceId: crypto.randomUUID(),
        reference,
        allocatedQuantity,
    });
    if (!result.ok || !result.plan) {
        return { ok: false, message: result.error || 'Die Rücknahme konnte nicht gebucht werden.' };
    }

    const plan = result.plan;
    const updatedServices = services.map((s) => (s.id === target.id ? plan.service : s));
    const note = {
        id: crypto.randomUUID(),
        text: plan.noteText,
        date: now,
        author: actorNameOf(command.user),
    };

    // `plan.log` ist das Protokoll, das gleich gespeichert wird — der Riegel
    // muss exakt diesen Stand sehen.
    const archiv = await withAutoArchive(fullProject.id, meta.globalData, updatedServices, plan.log, now, actorNameOf(command.user));

    // ── Die Zeile serverseitig entfernen, VOR dem Speichern ────────────────
    //
    // Der vierte Entfernpfad — und der einzige, der eine ARBEITSzeile
    // entfernt. Genau deshalb hat er bisher nicht funktioniert: der Riegel aus
    // MIGRATIONS.md Nr. 44 haengt jede fehlende Protokollzeile wieder an,
    // ausser sie traegt `BU-` oder `ALT-`. Eine zurueckgenommene Begehung
    // traegt keins von beidem — sie kam beim Speichern still zurueck, waehrend
    // `quantity_used` relational auf den neuen Stand ging. Das Ergebnis steht
    // als „bekannte Ausnahme" in Nr. 64 (Projekt 5355, Pos. 2.9.10: Zaehler 0,
    // Protokolleintrag unveraendert da).
    //
    // Vor dem Speichern, nicht danach: der Riegel vergleicht OLD gegen NEW.
    // Nach der RPC fehlt die Zeile schon in OLD, das anschliessende Speichern
    // ist unauffaellig. Umgekehrt saehe der Riegel sie in OLD und griffe ein.
    //
    // Ohne ausgerollte RPC meldet der Aufruf `legacy` und aendert nichts — dann
    // verhaelt sich der Pfad wie bisher.
    //
    // Bei der TEILWEISEN Ruecknahme ist die id-Liste leer: die Zeile bleibt
    // unter ihrer id stehen und traegt nur noch den Rest. Fuer den Riegel ist
    // das eine Zeile, die in OLD und NEW steht — nichts zu retten, kein
    // Grabstein.
    //
    // ── UND SEIT NR. 118: ALLES IN EINER TRANSAKTION ───────────────────────
    // `opts` wird IMMER mitgegeben, auch bei der Luecke (`gap`: keine ids,
    // nichts zu kuerzen, nur ein Zaehler). Damit laufen Versionspruefung,
    // Entfernung, Kuerzung und `project_services.quantity_used` serverseitig in
    // EINER Transaktion unter Zeilensperre — das schliesst das Fenster, in dem
    // Vorfall 26051025 entstand (Zeile weg, Zaehler blieb, weil der folgende
    // Save in die Bergung lief). Der Zaehler-Riegel Nr. 78 laeuft dabei und
    // sieht die bereits gesenkte Protokollsumme, weil die RPC `projects.data`
    // VOR `project_services` schreibt.
    //
    // Der anschliessende Save schreibt denselben Zaehler ein zweites Mal — mit
    // demselben Wert. Der Trigger von Nr. 78 feuert dann gar nicht: seine
    // `when`-Bedingung ist `new.quantity_used is distinct from old.quantity_used`
    // (project_services_counter_guard.sql). Idempotent, ohne Rettungszeile.
    //
    // Ohne ausgerollte Nr. 118 meldet der Aufruf `legacy` oder `rpc` und
    // `countersWritten: false` — dann macht der Save es wie bisher.
    //
    // NACHWEISZEILE (`plan.nachweis`): KEIN Zaehler an die RPC. Die Pauschale
    // wird nicht angefasst, und derselbe Wert noch einmal geschrieben waere
    // nicht folgenlos: weicht `project_services` vom Blob ab, liefe der
    // Zaehler-Riegel Nr. 78 an, die Nachpruefung der RPC wuerfe, und die
    // Entfernung des falschen Nachweises scheiterte an einer Abweichung, die
    // mit ihm nichts zu tun hat. Entfernung und Versionspruefung laufen
    // trotzdem in der einen Transaktion.
    let entfernung: ServiceLogRemovalOutcome;
    try {
        entfernung = await removeServiceLogEntries(
            fullProject.id,
            plan.removedEntryIds,
            `Rücknahme einer Fehlbuchung (${reference}): ${command.reason.trim()}`,
            {
                shortened: plan.reducedEntry
                    ? [{ id: plan.reducedEntry.id, quantity: plan.reducedEntry.to }]
                    : [],
                counters: plan.nachweis ? [] : [{ serviceId: target.id, quantityUsed: plan.service.quantityUsed }],
            },
        );
    } catch (e: any) {
        // Der Versionskonflikt kommt hier aus der RPC selbst (SQLSTATE 40001)
        // oder aus der Vorpruefung des Rueckfallwegs. In BEIDEN Faellen ist
        // nichts geschrieben — das sagt die Meldung, statt den Anwender raten
        // zu lassen.
        if (isProjectSaveConflict(e)) {
            return {
                ok: false,
                message: 'Das Projekt wurde zwischenzeitlich von anderer Stelle gespeichert. '
                    + 'Es wurde nichts geändert — weder das Protokoll noch der Leistungsstand. '
                    + 'Bitte das Projekt neu laden und die Rücknahme erneut prüfen.',
            };
        }
        return { ok: false, message: `Die Protokollzeile konnte nicht entfernt werden: ${e?.message || e}` };
    }

    // `onConflict: 'throw'` und NICHT die Bergung: trifft der Anker nicht,
    // behielte die Bergung zwar die gekuerzte oder entfernte Protokollzeile,
    // liesse aber die Zaehler weg (`zaehlerVomServer`, cloudService) — und
    // meldete Erfolg. Genau das war der Mechanismus von 26051025 (Zeile weg,
    // Zaehler blieb). Eine Ruecknahme, die nur zur Haelfte ankommt, ist
    // schlimmer als eine, die abbricht und neu geplant werden muss.
    try {
        await saveProjectToCloud(
            {
                ...fullProject.data,
                metadata: {
                    ...meta,
                    globalData: {
                        ...archiv.globalData,
                        services: updatedServices,
                        serviceLog: plan.log,
                        notes: [...archiv.notes, note, ...(meta.globalData?.notes || [])],
                    },
                },
            },
            fullProject.id,
            undefined,
            undefined,
            undefined,
            { onConflict: 'throw' },
        );
    } catch (e: any) {
        // ── Die entfernte Zeile im Journal stilllegen — bei JEDEM Fehlschlag ──
        //
        // Nicht nur beim Konflikt (Review 23.09.2026, P2-3): die JSON-Zeile ist
        // serverseitig weg, sobald die RPC sie genannt hat, und woran der Save
        // danach scheitert, aendert daran nichts — Netz, Riegel, Sperre, alles
        // gleich. Bliebe die Journalzeile lebend, stuende sie als `nur_journal`
        // in der Quarantaene, und dort hat „das Journal recht": eine Abrechnung
        // aus dem Journal forderte die Menge ein zweites Mal. Umgekehrt ist der
        // Preis gering — eine stillgelegte Zeile zu einer entfernten JSON-Zeile
        // ist genau richtig, und ein Wiederholungsversuch legt sie erneut still
        // (`voidJournalEntries` ist idempotent und wirft nie).
        //
        // Gefragt wird `entfernung.removedIds` und NICHT `plan.removedEntryIds`:
        // der Plan sagt, was entfernt werden SOLLTE. Im Rueckfall `legacy`
        // (Nr. 66 fehlt) ist nichts entfernt — dort haette die Entfernung erst
        // der Save gebracht, und der ist gerade gescheitert. Eine stillgelegte
        // Journalzeile zu einer weiterhin LEBENDEN JSON-Zeile waere der
        // Unterschuss, den niemand mehr sieht.
        if (entfernung.removedIds.length > 0) {
            await voidJournalEntries(fullProject.id, entfernung.removedIds, 'correction');
        }

        // ── Die GEKUERZTE Zeile: hier wird NICHTS ins Journal geschrieben ──────
        //
        // Codex-Befund 23.09.2026, P2-1: die JSON-Zeile traegt serverseitig
        // schon die gekuerzte Menge (die RPC aus Nr. 118 hat sie unter
        // Zeilensperre gesetzt), die gespiegelte Journalzeile traegt weiter die
        // alte. Das ist eine WERTABWEICHUNG — Klasse `wert_weicht_ab` der
        // Abweichungssicht `v_service_journal_drift` (Nr. 115).
        //
        // WARUM hier trotzdem nichts ins Journal geht (Entscheidung des
        // Betreibers, 23.09.2026): nach einem gescheiterten Save ist der Stand
        // des JSON auf dem Server NICHT bekannt. Der Save kann TEILWEISE
        // gelaufen sein — `projects.data` und die Kindtabellen sind getrennt
        // (`save_project_children_tx`) —, und ob der Blob die Kuerzung der RPC
        // stehen gelassen oder ueberschrieben hat, weiss diese Stelle nicht.
        // Eine Journalzeile mit einer GERATENEN Menge waere schlimmer als eine
        // sichtbare Wertabweichung: sie sieht richtig aus, niemand prueft sie
        // nach, und ab S5c (Abrechnung aus dem Journal) kostet sie Geld. Die
        // Abweichung dagegen meldet sich von selbst, und die naechste Ruecknahme
        // desselben Eintrags legt die alte Zeile still und haengt die DANN
        // gueltige Menge an.
        //
        // Deshalb steht hier nur eine Warnung — kein Schreibweg.
        if (entfernung.shortenedIds.length > 0) {
            console.warn(
                '[serviceReversalService] Speichern nach der Kürzung gescheitert: die JSON-Zeile ist gekürzt, '
                + 'die gespiegelte Journalzeile trägt noch die alte Menge — das ist die Klasse `wert_weicht_ab` '
                + 'der Abweichungssicht `v_service_journal_drift` (Nr. 115). Absichtlich NICHT ins Journal '
                + 'geschrieben: nach einem gescheiterten Save ist der Stand des JSON auf dem Server nicht bekannt, '
                + 'und eine geratene Menge wäre schlimmer als eine sichtbare Abweichung. Die nächste Rücknahme '
                + 'desselben Eintrags legt die alte Zeile still und hängt die dann gültige Menge an.',
                entfernung.shortenedIds,
            );
        }

        // ── Der Nachsatz: was serverseitig SCHON steht ─────────────────────────
        //
        // DREI Groessen entscheiden, nicht zwei (Codex-Befund 23.09.2026, P2-2):
        // `removedIds` (die Zeile ist weg), `shortenedIds` (die Zeile steht
        // gekuerzt da) und `countersWritten` (der Leistungsstand ist gesenkt).
        // Die bestaetigte KUERZUNG fehlte bisher — die teilweise Ruecknahme
        // bekam den Text der Entfernung oder den der Luecke, und beide
        // behaupten etwas ueber eine Zeile, die noch steht.
        //
        // Mit geschriebenem Zaehler (Nr. 118) sind Protokoll UND Leistungsstand
        // bereits angepasst, in EINER Transaktion; es fehlen nur noch die
        // Ruecknahmespur und die Notiz. Der Text „der Leistungsstand noch nicht
        // gesenkt" waere dort schlicht falsch und schickte die Buchhaltung zu
        // einer Korrektur, die doppelt abzoege.
        //
        // Entschieden wird an `entfernung.countersWritten` und NICHT am Modus
        // (Review 23.09.2026, P3-3): `atomic` heisst „die RPC lief", nicht „der
        // Zaehler steht". Hat der Server die Position als fehlend gemeldet
        // (`counters_missing`, unvollstaendige Kindtabellen oder eine
        // Positions-Kennung, die keine uuid ist), haengt der Leistungsstand
        // weiter am Save — dann gilt der vorsichtigere Text.
        //
        // Steht nur der Zaehler, ist es der Lueckenpfad (`gap`): dort gibt es
        // kein Protokoll, das angepasst waere (P3-4). Steht nichts davon
        // (`legacy`), ist wirklich nichts geschehen.
        //
        // Der Nachsatz gilt fuer BEIDE Ausgaenge — Versionskonflikt und
        // beliebiger Speicherfehler. Woran der Save gescheitert ist, aendert
        // nichts daran, was die RPC vorher schon geschrieben hat.
        //
        // Beim NACHWEIS gibt es keinen Leistungsstand, der haengen koennte (es
        // ging kein Zaehler mit, `countersWritten` ist immer false). Der Text
        // der Entfernung ohne Zaehler schickte die Buchhaltung sonst zu einer
        // „Menge ohne Protokolleintrag", die es nicht gibt.
        const nachsatz = entfernung.removedIds.length > 0 && plan.nachweis
            ? ' Die Nachweiszeile ist bereits entfernt (der Leistungsstand ist davon nicht berührt), nur die Spur und die Notiz fehlen — nach dem Neuladen prüfen.'
            : entfernung.removedIds.length > 0
            ? (entfernung.countersWritten
                ? ' Leistungsstand und Protokoll sind angepasst, nur die Spur und die Notiz fehlen — nach dem Neuladen prüfen.'
                : ' Die Protokollzeile ist bereits entfernt, der Leistungsstand noch nicht gesenkt — nach dem Neuladen erscheint die Menge als „ohne Protokolleintrag" und lässt sich dort zurücknehmen.')
            : entfernung.shortenedIds.length > 0
                ? (entfernung.countersWritten
                    ? ' Der Protokolleintrag ist bereits gekürzt und der Leistungsstand angepasst, nur die Spur und die Notiz fehlen — nach dem Neuladen prüfen.'
                    : ' Der Protokolleintrag ist bereits gekürzt, der Leistungsstand ist noch NICHT angepasst und muss geprüft werden.')
                : entfernung.countersWritten
                    ? ' Der Leistungsstand ist angepasst, nur die Spur und die Notiz fehlen — nach dem Neuladen prüfen.'
                    : ' Es wurde nichts geändert.';
        if (isProjectSaveConflict(e)) {
            return {
                ok: false,
                message: `Das Projekt wurde zwischenzeitlich von anderer Stelle gespeichert.${nachsatz} Bitte das Projekt neu laden und die Rücknahme erneut prüfen.`,
            };
        }
        const grund = String(e?.message || e || '').trim() || 'unbekannter Fehler';
        const punkt = grund.endsWith('.') ? '' : '.';
        return { ok: false, message: `Speichern fehlgeschlagen: ${grund}${punkt}${nachsatz}` };
    }

    // ── Zweitschrift ins Journal — nur die HINZUGEKOMMENE Zeile ────────────
    //
    // `plan.log` ist das ganze neue Protokoll; angehaengt wird ausschliesslich,
    // was vorher nicht darin stand (die Ruecknahmespur). Den ganzen Log zu
    // schicken waere bei jedem Vorgang ein Vielfaches an Nutzlast.
    //
    // Die Ruecknahmespur wird angehaengt, obwohl sie Menge 0 traegt: sie steht
    // wirklich im JSON, und ohne sie stuende eine JSON-Zeile am Ende weder im
    // Journal noch in der Quarantaene.
    const vorher: ServiceTransaction[] = getServiceLog({ metadata: meta });
    const bekannt = new Set(vorher.map(kennung));
    await mirrorEntriesToJournal({
        projectId: fullProject.id,
        entries: (plan.log || []).filter((e) => !bekannt.has(kennung(e))),
        previousLog: vorher,
        source: 'reversal',
        linkedProjectConfig: meta.globalData?.linkedProjectConfig ?? null,
    });

    // ── Die ENTFERNTE Zeile im Journal zuruecknehmen ───────────────────────
    //
    // Nimmt die Ruecknahme einen Protokolleintrag zurueck, verschwindet er aus
    // dem JSON. Das Journal kennt kein Delete — bliebe die gespiegelte Zeile
    // stehen, wiese es fuer immer eine Menge aus, die im Protokoll nicht mehr
    // steht, und die Abweichungssicht meldete sie fuer immer mit.
    //
    // Der Grund ist `correction` und ausdruecklich NICHT `storno`: `storno`
    // gehoert dem Weg, auf dem eine RECHNUNGSbuchung zurueckgenommen wird.
    // Hier wird eine FEHLBUCHUNG korrigiert — die Begehung ist ausgefallen,
    // der Bericht war eine Dublette, ein Zaehler hat Menge erfunden. Das sind
    // zwei verschiedene Vorgaenge, und sie spaeter auseinanderhalten zu koennen
    // ist der ganze Sinn der Gruende.
    //
    // Erst hier, nach dem bestaetigten Speichern: bricht der Pfad vorher ab
    // (Riegel, Sperre, Fehler), steht die Zeile noch im JSON und darf im
    // Journal nicht angetastet werden. Bei `target.kind === 'gap'` wird nichts
    // entfernt, die Liste ist dann leer und der Aufruf folgenlos.
    //
    // Nicht in einem `try` mit anwendersichtbarem `catch`: `voidJournalEntries`
    // wirft nie und das JSON ist zu diesem Zeitpunkt geschrieben — ein Fehler
    // hier gehoerte zu einem Vorgang, der gelungen ist.
    await voidJournalEntries(fullProject.id, plan.removedEntryIds, 'correction');

    // ── Die GEKUERZTE Zeile: alte stilllegen, DANN die gekuerzte einfuegen ─
    //    — in EINER Transaktion (SQL Nr. 136) ─────────────────────────────
    //
    // Das Journal kennt kein Update. Die gespiegelte Zeile mit der alten
    // Menge wird durch eine NEUE ersetzt: abgeleiteter Schluessel (die id ist
    // im Journal vergeben — `knownIds` traegt sie, damit `journalIdDecision`
    // sie als besetzt sieht), JSON-id in `legacy_entry_id`. Beim Lesen kommt
    // sie von dort zurueck, die Zuordnungen der Rechnungen finden ihren
    // Eintrag weiter, und die Abweichungssicht paart sie ueber
    // `coalesce(legacy_entry_id, id)`.
    //
    // War das Journal nicht lesbar, geschieht hier nichts: die alte Zeile
    // bleibt, die Abweichungssicht meldet sie, die naechste Ruecknahme heilt.
    if (plan.reducedEntry) {
        await spiegleGekuerzteZeile({
            projectId: fullProject.id,
            log: plan.log || [],
            reduziert: plan.reducedEntry,
            vorher,
            linkedProjectConfig: meta.globalData?.linkedProjectConfig ?? null,
        });
    }

    return { ok: true, message: plan.noteText + archiv.hinweis };
};

export interface GekuerzteZeileSpiegeln {
    projectId: string;
    /** Das gespeicherte Protokoll (`plan.log`). */
    log: ServiceTransaction[];
    /** Welche Zeile auf welche Menge gekuerzt wurde (`plan.reducedEntry`). */
    reduziert: { id: string; from: number; to: number };
    /** Das Protokoll VOR dem Vorgang — liefert die vergebenen ids. */
    vorher: ServiceTransaction[];
    linkedProjectConfig: LinkedProjectConfig | null;
}

/** Rundungsgrenze der Mengen (drei Stellen, wie `round3` der Logik). */
const MENGEN_TOLERANZ = 0.0005;

/**
 * SQLSTATE 55000 (`object_not_in_prerequisite_state`) aus Nr. 136: der Stand
 * im Journal hat sich zwischen Lesen und Ersetzen geaendert (paralleler
 * zweiter Aufruf, Stilllegung auf einem anderen Weg). Nichts geschrieben — und
 * bewusst nicht wiederholt (siehe `ersetzeGekuerzteZeile`).
 */
const istZustandGeaendert = (error: unknown): boolean =>
    String((error as { code?: unknown } | null | undefined)?.code ?? '') === '55000';

/**
 * Die Journal-Haelfte der teilweisen Ruecknahme — exportiert, damit die
 * Riegel vor dem Ersetzen ohne den ganzen Vorgang pruefbar sind.
 *
 * ─── Erst die Eindeutigkeit (Review 30.09.2026, Opus P3-1) ────────────────
 * Steht die id im Protokoll VORHER oder NACHHER mehr als einmal, wird nicht
 * ersetzt: die lebenden Journalzeilen antworten dann auf ZWEI JSON-Zeilen, die
 * Funktion legte beide still und fuegte nur eine ein. `planReversal` weist die
 * Dublette schon ab (Quarantaeneklasse `doppelt_im_projekt`); diese Stelle
 * haelt es trotzdem fest, weil sie das Journal schreibt. Ebenso muss die
 * gekuerzte Zeile im gespeicherten Protokoll genau einmal und mit genau der
 * Restmenge stehen — gespiegelt wird die Zeile, die WIRKLICH gekuerzt wurde,
 * nicht „irgendeine mit dieser id". Sonst: Warnung und Meldung in
 * `bug_reports`, wie beim gescheiterten Anhaengen; die alte Journalzeile bleibt
 * lebend, die Abweichungssicht meldet sie.
 *
 * Wirft nie: das JSON ist gespeichert, der Vorgang ist gelungen.
 */
export const spiegleGekuerzteZeile = async (args: GekuerzteZeileSpiegeln): Promise<void> => {
    const { projectId, log, reduziert, vorher, linkedProjectConfig } = args;
    const id = String(reduziert?.id ?? '').trim();
    const mitDieserId = (liste: ServiceTransaction[]) =>
        (liste || []).filter((e) => String(e?.id ?? '').trim() === id);
    const vorherTreffer = mitDieserId(vorher);
    const nachherTreffer = mitDieserId(log);
    const gekuerzt = nachherTreffer.length === 1
        && Math.abs(Number(nachherTreffer[0]?.quantity) - Number(reduziert?.to)) <= MENGEN_TOLERANZ
        ? nachherTreffer[0]
        : undefined;

    if (!id || vorherTreffer.length !== 1 || !gekuerzt) {
        const grund = vorherTreffer.length > 1 || nachherTreffer.length > 1
            ? `die id ${id} steht mehrfach im Protokoll (vorher ${vorherTreffer.length}, nachher ${nachherTreffer.length}) `
              + '— Quarantäneklasse doppelt_im_projekt'
            : `die gekürzte Zeile ${id || '(ohne id)'} steht nicht eindeutig mit der Restmenge ${reduziert?.to} `
              + 'im gespeicherten Protokoll';
        console.warn(
            `[serviceReversalService] Gekürzte Zeile NICHT im Journal ersetzt: ${grund}. `
            + 'Die alte Journalzeile bleibt lebend, die Abweichungssicht meldet sie.',
        );
        void meldeSpiegelFehler(projectId, [], new Error(`Teilrücknahme nicht gespiegelt: ${grund}`), 'Ersetzen');
        return;
    }

    const alte = await ladeLebendeJournalzeilenZu(projectId, id);
    if (!alte.journalGelesen) {
        console.warn('[serviceReversalService] Journal nicht lesbar — gekürzte Zeile nicht gespiegelt, die Abweichungssicht meldet sie.');
        return;
    }
    await ersetzeGekuerzteZeile({
        projectId,
        gekuerzt,
        alteZeilen: alte.zeilen.map((z) => ({
            id: z.id,
            quantity: Number(z.quantity),
            service_id: String(z.service_id ?? '').trim(),
        })),
        mengeVorher: Number(reduziert.from),
        vorher,
        linkedProjectConfig,
    });
};

interface GekuerzteZeileErsetzen {
    projectId: string;
    /** Die gekuerzte serviceLog-Zeile, wie sie gespeichert wurde. */
    gekuerzt: ServiceTransaction;
    /** Alle lebenden Journalzeilen zur JSON-id — JOURNAL-id, Menge und Position, wie geladen. */
    alteZeilen: Array<{ id: string; quantity: number; service_id: string }>;
    /**
     * Die Menge, die die serviceLog-Zeile VOR dieser Kuerzung trug
     * (`reducedEntry.from`) — der erwartete alte Stand im Journal.
     */
    mengeVorher: number;
    /** Das Protokoll VOR dem Vorgang — liefert die vergebenen ids. */
    vorher: ServiceTransaction[];
    linkedProjectConfig: LinkedProjectConfig | null;
}

/**
 * Das Ersetzen selbst.
 *
 * ─── Der Befund, wegen dessen es SQL Nr. 136 gibt ─────────────────────────
 * Bis 30.09.2026 stand hier ein Zweischritt: gekuerzte Zeile ANHAENGEN, dann
 * die alte STILLLEGEN. Traegt die Zeile einen `report_ref` (Berichtsbuchung),
 * verletzt die Ersatzzeile den Teil-Unique-Index
 * `service_journal_bericht_leistung_uq` (Nr. 59) — die alte lebt ja noch.
 * Ergebnis 23505, nichts stillgelegt: JSON gekuerzt, Journal mit der alten
 * Menge (PROD: zwei Faelle in 26051025). Die umgekehrte Reihenfolge in ZWEI
 * Aufrufen haette bei einem Fehlschlag dazwischen KEINE lebende Zeile
 * hinterlassen (Unterschuss, Fable-Review 23.09.2026). Richtig ist
 * „stilllegen, dann einfuegen" nur in EINER Transaktion — serverseitig,
 * `ersetzeJournalzeilen`.
 *
 * ─── Was stillgelegt wird ─────────────────────────────────────────────────
 * ALLE lebenden Zeilen zur JSON-id (nach einem frueheren Fehlschlag koennen es
 * zwei sein), ueber ihre JOURNAL-ids — ausser der Ersatzzeile selbst: steht
 * sie mit genau diesem Inhalt schon lebend da (derselbe abgeleitete
 * Schluessel), bleibt sie stehen, und die Funktion wuerde „dieselbe id alt und
 * neu" als Widerspruch abweisen.
 *
 * ─── Der erwartete alte Stand (Codex-Review R3, 30.09.2026) ───────────────
 * Zwei Kuerzungen derselben Zeile kurz nacheinander: A (4 → 3) laedt die
 * lebenden Journalzeilen erst, NACHDEM B (3 → 2) seine Spiegelung
 * abgeschlossen hat, und saehe B's Zeile (2). Ersetzte A sie, truege das
 * Journal 3, obwohl das JSON 2 hat. Deshalb muss jede zu ersetzende Zeile die
 * Menge VOR dieser Kuerzung tragen (`mengeVorher`):
 *   - hier VOR dem Aufruf billig geprueft — sonst gar kein Aufruf, nur Warnung
 *     und Meldung in `bug_reports` (auch kein Rueckfall-Zweischritt);
 *   - und in der Funktion noch einmal ATOMAR unter der Zeilensperre (`p_old`
 *     traegt die Erwartung je Zeile; Abweichung → 55000, nichts geschrieben).
 * Seit 02.10.2026 gehoert zum erwarteten Stand auch die POSITION der
 * gekuerzten Zeile — nur in der Pruefung hier, Nr. 136 kennt sie nicht
 * (Begruendung an der Stelle).
 * Eine Folge: der Rest eines frueheren Zweischritts mit zwei VERSCHIEDENEN
 * Mengen (`mehrfach_lebend`) wird hier nicht mehr still geheilt, sondern
 * gemeldet — ob die abweichende Zeile alt oder neu ist, weiss diese Stelle
 * nicht.
 *
 * ─── Die Ausgaenge ────────────────────────────────────────────────────────
 * - gelungen: fertig.
 * - Zustand geaendert (55000, z. B. ein paralleler zweiter Aufruf oder eine
 *   Stilllegung auf einem anderen Weg): nur warnen, KEINE Wiederholung —
 *   die Restmenge dieses Vorgangs kann ueberholt sein (Begruendung an der
 *   Stelle; Codex-Review R2 30.09.2026). Nichts geschrieben, in `bug_reports`
 *   gemeldet, eine Restabweichung zeigt der Nachtlauf.
 * - Funktion fehlt (PGRST202, Nr. 136 nicht gefahren): der alte Zweischritt
 *   als Rueckfall, mit Warnung. ERWARTETE_MIGRATION bleibt, der Client darf
 *   vor dem SQL raus.
 * - abgelehnt/gescheitert: NICHTS geschrieben (eine Transaktion). Kein
 *   Rueckfall auf den Zweischritt — der schriebe genau den Halbzustand, den die
 *   Funktion gerade verhindert hat. Die alte Zeile bleibt lebend mit der alten
 *   Menge (`wert_weicht_ab`, Nr. 115), die naechste Ruecknahme desselben
 *   Eintrags ersetzt sie.
 *
 * Nichts davon geht in den Dialog: das JSON ist gespeichert, der Vorgang ist
 * gelungen (Review 23.09.2026, P3-2). Wirft nie.
 */
const ersetzeGekuerzteZeile = async (args: GekuerzteZeileErsetzen): Promise<void> => {
    const { projectId, gekuerzt, alteZeilen, mengeVorher, vorher, linkedProjectConfig } = args;
    const knownIds = vorher.map((e) => String(e?.id ?? '').trim()).filter(Boolean);
    const zeilen = toJournalRows({ projectId, entries: [gekuerzt], source: 'reversal', linkedProjectConfig, knownIds });
    const neueId = zeilen[0]?.id;
    const zuErsetzen = alteZeilen.filter((z) => z.id !== neueId);
    const stillzulegen = zuErsetzen.map((z) => z.id);

    // ── Der erwartete alte Stand, VOR dem Aufruf (Begruendung oben) ────────
    // Traegt eine zu ersetzende Zeile nicht MENGE UND POSITION vor dieser
    // Kuerzung, hat ein anderer Vorgang das Journal seit dem Speichern
    // geaendert. Dann weder ersetzen noch (Rueckfall) anhaengen — beides
    // schriebe einen ueberholten Stand. Nur melden; eine Restabweichung zeigt
    // der Nachtlauf.
    //
    // Die Position gehoert dazu (Codex-Review des Zuordnungsumzugs
    // 02.10.2026, P2): Nr. 136 prueft nur die Menge, und die Kuerzung aendert
    // die Position nie — die gekuerzte Zeile steht, wo sie stand. Eine
    // verspaetete Kuerzung (4 → 3) nach einem Umzug derselben Zeile A→B laede
    // die lebende B-Zeile, ihre Menge passte, und ersetzt wuerde sie durch eine
    // Zeile auf A, waehrend das JSON B traegt. Der Preis: eine Zeile, die im
    // Journal schon auf der falschen Position steht (Rest einer frueheren
    // Drift, Typ 6290-1), heilt die naechste Kuerzung nicht mehr still mit —
    // sie wird gemeldet und per Fix-SQL bereinigt. Dieselbe Linie wie beim
    // Umzug selbst (backfillMoveJournal).
    const positionVorher = String(gekuerzt.serviceId ?? '').trim();
    const fremderStand = zuErsetzen.filter((z) =>
        !(Math.abs(Number(z.quantity) - mengeVorher) <= MENGEN_TOLERANZ)
        || String(z.service_id ?? '').trim() !== positionVorher);
    if (fremderStand.length > 0) {
        const grund = `${fremderStand.length} lebende Journalzeile(n) tragen nicht den Stand vor dieser Kürzung `
            + `(${mengeVorher} auf ${positionVorher}): `
            + fremderStand.map((z) => `${z.id} = ${z.quantity} auf ${z.service_id}`).join(', ');
        console.warn(
            `[serviceReversalService] Gekürzte Zeile NICHT im Journal ersetzt: das Journal hat sich seit dem Laden geändert — ${grund}. `
            + 'Kein Aufruf, kein Rückfall; eine verbliebene Abweichung zeigt der Nachtlauf (Wertabweichungen).',
        );
        void meldeSpiegelFehler(projectId, zeilen, new Error(`Teilrücknahme nicht gespiegelt: ${grund}`), 'Ersetzen');
        return;
    }

    // Nur wenn es etwas zu ERSETZEN gibt. Lebt keine alte Zeile (nie
    // gespiegelt, oder die Ersatzzeile steht schon) oder ist die Zeile nicht
    // abbildbar, bleibt es beim Anhaengen — das kann den Riegel nicht treffen,
    // weil zu diesem Eintrag keine andere Zeile lebt.
    if (zeilen.length > 0 && stillzulegen.length > 0) {
        const ersetzt = await ersetzeJournalzeilen(
            projectId,
            zuErsetzen.map((z) => ({ id: z.id, quantity: mengeVorher })),
            'correction',
            zeilen,
        );
        if (ersetzt.ok) return;
        // ── 55000: der Stand wurde von einem ANDEREN Aufruf geaendert ──────
        // KEIN Neuladen, KEINE Wiederholung (Codex-Review R2, 30.09.2026, P2;
        // Entscheidung Controller). Die Ersatzzeile traegt die Restmenge
        // DIESES Vorgangs — und die kann ueberholt sein: zwei Kuerzungen
        // derselben Zeile kurz nacheinander (4 → 3, dann 3 → 2), die zweite
        // ersetzt zuerst; eine Wiederholung der verspaeteten ersten legte die
        // frisch geladene Zeile (2) still und schriebe wieder 3, obwohl das
        // JSON 2 traegt. Das waere eine falsche Menge, die richtig aussieht.
        // Geschrieben ist bei 55000 nichts (eine Transaktion); gemeldet ist es
        // schon (`ersetzeJournalzeilen` schreibt bei jedem Fehler ausser
        // PGRST202 nach `bug_reports`). Eine verbliebene Abweichung zeigt der
        // Nachtlauf (Zeile „Wertabweichungen").
        if (istZustandGeaendert(ersetzt.error)) {
            console.warn(
                '[serviceReversalService] Ersetzen der gekürzten Zeile abgelehnt (55000): der Stand im Journal wurde von '
                + 'einem anderen Aufruf geändert. Absichtlich KEINE Wiederholung — die Restmenge dieses Vorgangs kann '
                + 'überholt sein. Nichts geschrieben; eine verbliebene Abweichung zeigt der Nachtlauf (Wertabweichungen).',
                ersetzt.error,
            );
            return;
        }
        if (ersetzt.reason !== 'rpc_fehlt') {
            console.warn(
                '[serviceReversalService] Ersetzen der gekürzten Zeile im Journal gescheitert — die alte Zeile bleibt lebend '
                + 'mit der alten Menge (`wert_weicht_ab`, Nr. 115); die nächste Rücknahme desselben Eintrags ersetzt sie.',
                ersetzt.error,
            );
            return;
        }
        console.warn(
            '[serviceReversalService] service_journal_ersetzen (Nr. 136) fehlt — Rückfall auf „anhängen, dann stilllegen“. '
            + 'Bei einer Berichtszeile scheitert das Anhängen am Eindeutigkeitsriegel (23505), die alte Zeile bleibt lebend.',
        );
    }
    await anhaengenDannStilllegen({ projectId, gekuerzt, stillzulegen, vorher, linkedProjectConfig });
};

/**
 * Der RUECKFALL ohne Nr. 136 — der Zweischritt von vor dem 30.09.2026,
 * unveraendert: anhaengen ZUERST, stilllegen DANACH (wegen der
 * Ausfallrichtung: scheitert das Anhaengen, bleibt die alte Zeile allein und
 * lebend — `wert_weicht_ab`; umgekehrt bliebe KEINE lebende Zeile). OHNE
 * Ausgangskorb (`mirrorEntriesToJournalOhneKorb`): kaeme die gekuerzte Zeile
 * Stunden spaeter aus dem Korb nach, stuende sie neben der alten, die dann
 * niemand mehr stilllegt.
 */
const anhaengenDannStilllegen = async (args: {
    projectId: string;
    gekuerzt: ServiceTransaction;
    stillzulegen: string[];
    vorher: ServiceTransaction[];
    linkedProjectConfig: LinkedProjectConfig | null;
}): Promise<void> => {
    const { projectId, gekuerzt, stillzulegen, vorher, linkedProjectConfig } = args;
    const angehaengt = await mirrorEntriesToJournalOhneKorb({
        projectId,
        entries: [gekuerzt],
        previousLog: vorher,
        source: 'reversal',
        linkedProjectConfig,
    });
    if (!angehaengt.written) {
        console.warn(
            '[serviceReversalService] Gekürzte Zeile nicht ins Journal geschrieben — die alte bleibt lebend, die Abweichungssicht meldet sie.',
            angehaengt.reason,
        );
        return;
    }
    const stillgelegt = await voidJournalEntries(projectId, stillzulegen, 'correction', { nurJournalIds: true });
    // Bleibt eine alte Zeile lebend, stehen zwei Zeilen zu derselben JSON-id
    // im Journal — die Quarantaeneklasse `mehrfach_lebend` (Nr. 115). Sie
    // kostet kein Geld, solange das JSON fuehrt, waere aber ab S5c eine
    // doppelte Menge.
    if (stillgelegt.error || stillgelegt.voided < stillzulegen.length) {
        console.warn(
            '[serviceReversalService] Die alte Journalzeile der gekürzten Zeile wurde nicht (vollständig) stillgelegt — '
            + `zwei lebende Zeilen zu derselben id sind \`mehrfach_lebend\` (Nr. 115). Erwartet ${stillzulegen.length}, stillgelegt ${stillgelegt.voided}.`,
            stillgelegt.error ?? stillgelegt.unbekannt,
        );
    }
};
